/* <product-request>: the product inquiry dialog (snippets/product-request.liquid) on the shared
 * AsansaraDrawer lifecycle; the snippet's CSS centers it on wider screens and slides it in full
 * screen from the right on phones. Any [data-product-request-open] whose aria-controls names the
 * dialog opens it for the variant in its data-product-request-variant. The trigger is a link to
 * the Contact page: it stays one until this script runs, and for modified clicks.
 *
 * The contact form posts the normal way, once: Shopify's storefront contact script (its spam
 * protection) takes every /contact submit over and posts it itself, whatever this script does,
 * so a background post here would send the request twice. Shopify then renders this product page
 * with the result and the dialog reopens showing the thank-you or Shopify's errors.
 *
 * Meanwhile only Send waits. The dialog can always be closed (that does not stop a post already
 * made) and keeps what was typed. If this page is still here after STALLED_MS, the outcome is
 * unknown: Shopify's script may still post (it can defer), the answer may be slow, or the post
 * never left (the script failed, the navigation was stopped). Nothing is re-sent on its own and
 * Send does not quietly come back: a note under it says this is taking longer, and only then can
 * the shopper press Send again, on purpose. From the first Send until this page is left, the
 * dialog keeps the piece it was sent for, since a deferred post takes the form as it is then. */
if (!customElements.get('product-request') && window.AsansaraDrawer) {
  const STALLED_MS = 10000;

  // The result a rendered form carries: the thank-you shown, or Shopify's error list.
  const resultOf = (form) => {
    if (form.querySelector('[data-product-request-done]:not([hidden])')) return { sent: true };
    const error = form.querySelector('[data-product-request-error]');
    return error?.textContent.trim() ? { error: error.childNodes } : null;
  };

  customElements.define('product-request', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form) return;
      this.compose = this.querySelector('[data-product-request-compose]');
      this.done = this.querySelector('[data-product-request-done]');
      this.doneTitle = this.done.querySelector('[tabindex="-1"]');
      this.error = this.querySelector('[data-product-request-error]');
      this.meta = this.querySelector('[data-product-request-meta]');
      this.image = this.querySelector('[data-product-request-image]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submit.textContent.trim();
      this.note = this.querySelector('[data-product-request-note]');
      this.status = 'idle';
      this.variants = this.readVariants();
      this.drawer = new window.AsansaraDrawer(this.dialog, '[data-product-request-close]');
      this.events = new AbortController();
      const signal = this.events.signal;

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-product-request-open]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id) return;
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        // Once sent, the form may still be posted as it stands: keep the piece it was sent for.
        if (this.status === 'idle') this.showVariant(trigger.dataset.productRequestVariant);
        this.drawer.open(trigger);
      }, { signal });
      this.form.addEventListener('submit', () => this.onSubmit(), { signal });
      this.dialog.addEventListener('close', () => this.startOver(), { signal });
      // Back from the normal post (challenge page) restored from the back/forward cache: that post
      // has left this page, so nothing here can still send it.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.setStatus('idle');
      }, { signal });

      const result = resultOf(this.form);
      if (result) this.reopen(result);
    }

    disconnectedCallback() {
      clearTimeout(this.stallTimer);
      this.events?.abort();
      this.drawer?.destroy();
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('script[data-product-request-variants]')?.textContent || '{}');
      } catch {
        return {};
      }
    }

    showVariant(id) {
      const variant = this.variants[id];
      if (!variant) return;
      this.form.querySelectorAll('[data-product-request-field]').forEach((input) => {
        input.value = variant[input.dataset.productRequestField] || '';
        input.disabled = !input.value;
      });
      if (this.meta) this.meta.textContent = variant.meta;
      if (this.image && variant.thumb) this.image.src = variant.thumb;
    }

    // Shopify rendered the result of a normal post: show it, and drop Shopify's markers and the
    // fields it echoes into the query (the message must not stay in the address bar or history), so
    // that a reload does not bring the dialog back. After an error Shopify comes back without the
    // variant, and the page falls back to its first one; the echoed Product URL still names the
    // variant that was asked about, so the dialog and the page return to it.
    reopen(result) {
      const url = new URL(location.href);
      const asked = url.searchParams.get('contact[Product URL]');
      const variantId = asked ? new URL(asked, location.origin).searchParams.get('variant') : null;
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key === 'contact_posted' || key === 'form_type' || key.startsWith('contact[')) url.searchParams.delete(key);
      });
      if (url.hash === `#${this.form.id}`) url.hash = '';
      history.replaceState(history.state, '', url);
      if (!result.sent && this.variants[variantId]) this.restoreVariant(variantId);
      const trigger = document.querySelector(`[data-product-request-open][aria-controls="${CSS.escape(this.dialog.id)}"]`);
      this.drawer.open(trigger || document.activeElement, result.sent ? this.doneTitle : this.error);
    }

    // Checks the page's option pills for the variant and lets product-form (its change listener)
    // bring price, gallery, URL and the trigger along; the dialog's card and fields follow at once.
    restoreVariant(id) {
      this.showVariant(id);
      const options = this.variants[id].options || [];
      const section = this.closest('.shopify-section') || document;
      let changed = null;
      options.forEach((value, index) => {
        const radio = Array.from(section.querySelectorAll(`input[type="radio"][data-option-position="${index + 1}"]`))
          .find((input) => input.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = radio;
        }
      });
      if (changed) customElements.whenDefined('product-form').then(() => changed.dispatchEvent(new Event('change', { bubbles: true })));
    }

    // The post itself is left to the browser and Shopify.
    onSubmit() {
      this.showError('');
      this.setStatus('sending');
    }

    // idle: nothing sent from this page. sending: a disabled Send fires no second submit, which
    // Shopify's script would post again; the same holds for Enter in a field (no implicit
    // submission with a disabled default button). stalled: the post may or may not still happen,
    // so the note says so and Send beside it is enabled for a deliberate second try.
    setStatus(status) {
      clearTimeout(this.stallTimer);
      this.status = status;
      const sending = status === 'sending';
      this.submit.disabled = sending;
      if (sending) this.submit.setAttribute('aria-busy', 'true');
      else this.submit.removeAttribute('aria-busy');
      this.submit.textContent = sending ? this.dataset.sendingLabel : this.submitLabel;
      this.note.textContent = status === 'stalled' ? this.dataset.stalledNote : '';
      if (sending) this.stallTimer = setTimeout(() => this.setStatus('stalled'), STALLED_MS);
    }

    // Text, or nodes of Shopify's error list from the answer page; empty hides the message.
    showError(content) {
      const nodes = typeof content === 'string' ? [content] : Array.from(content, (node) => document.importNode(node, true));
      this.error.replaceChildren(...nodes);
      this.error.hidden = !this.error.textContent.trim();
    }

    // Closing drops a shown result. After the thank-you the next opening starts a new message and
    // keeps the contact details; an unsent message stays as typed.
    startOver() {
      this.showError('');
      if (this.done.hidden) return;
      this.done.hidden = true;
      this.compose.hidden = false;
      this.form.elements['contact[body]'].value = '';
    }
  });
}
